import { ConfigProvider, Radio, RadioChangeEvent } from 'antd';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import { setPageLoading } from 'widgets/PageLoading';
import { toast } from 'react-toastify';
import { getMessageError } from 'services/response';
import { Colors, InputSearch, Row, Text } from 'basic-ui-lib';
import useGetAllScreens from 'modules/playlist/services/hooks/useGetAllScreen';
import ItemScreen from './ItemScreen';
import { IScreen } from 'modules/dashboard/interfaces/api';
import playlistApis from 'modules/playlist/services/apis';

export interface SetToScreenModalRef {
  open: (playListId: string) => void;
}

interface Props {}

const SetToScreenModal = forwardRef<SetToScreenModalRef, Props>(({}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [playListId, setPlayListId] = useState('');
  const [type, setType] = useState<string>('OFFLINE');
  const [screens] = useGetAllScreens();
  const [searchValue, setSearchValue] = useState('');
  const [listScreens, setListScreens] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    open: (playListId: string) => {
      setPlayListId(playListId);
      setIsOpen(true);
    },
  }));

  const onClose = () => {
    setListScreens([]);
    setIsOpen(false);
  };

  const handleChangeType = (e: RadioChangeEvent) => {
    setType(e.target.value);
  };

  const filteredScreens: IScreen[] = useMemo(() => {
    let result = screens;

    result = result.filter((screen) => (type === 'ONLINE' ? screen.status === 'ONLINE' : screen.status !== 'ONLINE'));

    if (searchValue.trim()) {
      const keyword = searchValue.toLowerCase();
      result = result.filter(
        (screen) =>
          screen?.playerName?.toLowerCase().includes(keyword) || screen?.businessName?.toLowerCase().includes(keyword),
      );
    }

    return result;
  }, [screens, type, searchValue]);

  const handleChooseScreen = (screenId: string, checked: boolean) => {
    if (checked) {
      setListScreens((prev) => [...prev, screenId]);
    } else {
      setListScreens((prev) => prev.filter((screen) => screen !== screenId));
    }
  };

  const handleSubmit = async () => {
    try {
      setPageLoading(true);

      const res = await playlistApis.setScreen(playListId, listScreens);
      if (res?.data?.data) {
        toast.success('Set to screens successfully');
        onClose();
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to set to screens'));
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <BaseModal
      title='Set To Screen'
      isOpen={isOpen}
      width={1000}
      onClose={onClose}
      okText='Continue'
      onOk={handleSubmit}
    >
      <Container>
        <InputSearch
          placeholder='Search screens by name, location,...'
          value={searchValue}
          onChange={(value) => setSearchValue(value)}
        />
        <Row gap={16}>
          <Text content_2 semibold color='neutral.text.3'>
            SCREENS
          </Text>
          <ConfigProvider
            theme={{
              token: {
                fontWeightStrong: 600,
              },
              components: {
                Radio: {
                  colorBgTextActive: Colors.neutral.bg[1],
                  buttonCheckedBg: Colors.primary[5],
                  colorPrimaryHover: Colors.primary[5],
                  buttonBg: Colors.neutral.bg[4],
                },
              },
            }}
          >
            <Radio.Group onChange={handleChangeType} value={type} optionType='button' buttonStyle='solid'>
              <Radio.Button value='OFFLINE' className='radio-type'>
                Offline
              </Radio.Button>
              <Radio.Button value='ONLINE' className='radio-type'>
                Online
              </Radio.Button>
            </Radio.Group>
          </ConfigProvider>
        </Row>
        <Content>
          {filteredScreens.map((screen) => (
            <ItemScreen
              key={screen.id}
              screen={screen}
              checked={listScreens.includes(screen.id)}
              onChange={(checked) => handleChooseScreen(screen.id, checked)}
            />
          ))}
        </Content>
      </Container>
    </BaseModal>
  );
});

SetToScreenModal.displayName = 'SetToScreenModal';
export default SetToScreenModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 0 24px 16px;

  .radio-type .ant-radio-button-label {
    font-weight: 500;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
`;
